import { Outlet, useLocation } from "react-router-dom"
import { useState, useEffect, useCallback, Suspense } from "react";
import { useSelector } from "react-redux";
import Header from "./Header"
import Sidebar from "./Sidebar";
import { ToastContainer } from 'react-toastify';
import SkeletonTable from '../components/commons/SkeletonTable';
import { setSession } from '../services/auth';
import ErrorBoundary from '../components/commons/ErrorBoundary';
import useRealtimeSubscriptions from '../hooks/useRealtimeSubscriptions';
import { useNotifications } from '../hooks/useNotifications';
import AdvisorNotesDrawer from '../components/commons/AdvisorNotesDrawer';



export default function DefaultLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notesDrawerOpen, setNotesDrawerOpen] = useState(false);
  const { session } = useSelector((state) => state.auth);
  const location = useLocation();
  const isChatPage = location.pathname.includes('/chats');

  // Listen for sidebar toggling from sub-pages like Chats
  useEffect(() => {
    const handleToggle = () => setSidebarOpen(prev => !prev);
    window.addEventListener("toggle-sidebar", handleToggle);
    return () => window.removeEventListener("toggle-sidebar", handleToggle);
  }, []);

  // Shared sidebar collapsed state (persisted in localStorage)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem('sidebar-collapsed');
    return stored === 'true';
  });

  const toggleSidebarCollapse = useCallback(() => {
    setIsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebar-collapsed', next.toString());
      return next;
    });
  }, []);

  // ─── Native OS notifications via Web Notifications API ───
  const userId = session?.user?.id;
  const { permission, requestPermission, sendNotification } = useNotifications(userId);
  const [bannerDismissed, setBannerDismissed] = useState(() => localStorage.getItem('notif-banner-dismissed') === 'true');

  // ─── Global Realtime subscriptions (toasts for payments & AP orders) ───
  useRealtimeSubscriptions({ sendNotification });
  
  // Impersonation Banner Logic — reactive to session changes
  const [impersonatingData, setImpersonatingData] = useState(null);

  useEffect(() => {
    // Check localStorage whenever the session changes
    try {
      const backup = localStorage.getItem('admin_session_backup');
      if (backup) {
        const parsed = JSON.parse(backup);
        // Only show banner if we're logged in as a DIFFERENT user than the admin backup
        if (session?.user?.email && parsed.admin_email && session.user.email !== parsed.admin_email) {
          setImpersonatingData(parsed);
        } else {
          setImpersonatingData(null);
        }
      } else {
        setImpersonatingData(null);
      }
    } catch(e) {
      setImpersonatingData(null);
    }
  }, [session]);

  const handleReturnToAdmin = async () => {
      try {
          if (!impersonatingData) return;
          const { error } = await setSession(
              impersonatingData.access_token,
              impersonatingData.refresh_token
          );
          if (error) throw error;
          
          localStorage.removeItem('admin_session_backup');
          setImpersonatingData(null);
          window.location.href = '/users';
      } catch (err) {
          console.error('Failed to restore admin session:', err);
          alert('Error al restaurar sesión. Por favor inicia sesión manualmente.');
          localStorage.removeItem('admin_session_backup');
          setImpersonatingData(null);
          window.location.href = '/login';
      }
  };

  return <div className="dark:bg-boxdark-2 dark:text-bodydark">
    <div className="flex flex-col h-screen overflow-hidden">

      {/* Impersonation Banner — full width */}
      {impersonatingData && (
          <div className="bg-gradient-to-r from-amber-600 to-amber-500 text-white px-6 py-3 flex items-center justify-between shadow-lg z-[9999] animate-in slide-in-from-top-2 duration-300">
              <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                      <span className="material-symbols-outlined text-lg">admin_panel_settings</span>
                  </div>
                  <div>
                      <span className="text-sm font-bold block leading-tight">
                          Modo Administrador Activo
                      </span>
                      <span className="text-[11px] text-amber-100 leading-tight">
                          Estás viendo la plataforma como <strong className="text-white">{session?.user?.email}</strong>
                      </span>
                  </div>
              </div>
              <button 
                  onClick={handleReturnToAdmin}
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg text-xs font-bold transition-all hover:scale-[1.02] shadow-sm"
              >
                  <span className="material-symbols-outlined text-[16px]">switch_account</span>
                  Regresar a mi cuenta ({impersonatingData.admin_email})
              </button>
          </div>
      )}

      {/* Notification Permission Banner — full width */}
      {permission === 'default' && !bannerDismissed && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100 px-6 py-2.5 flex items-center justify-center gap-3 z-[9998] animate-in slide-in-from-top-1 duration-200">
          <span className="text-lg">🔔</span>
          <span className="text-sm text-slate-600">
            Activa las notificaciones para recibir alertas en tu escritorio.
          </span>
          <button
            onClick={async () => {
              await requestPermission();
            }}
            className="px-3 py-1 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90 transition-colors shadow-sm"
          >
            Activar
          </button>
          <button
            onClick={() => {
              localStorage.setItem('notif-banner-dismissed', 'true');
              setBannerDismissed(true);
            }}
            className="text-slate-400 hover:text-slate-600 text-xs"
          >
            Ahora no
          </button>
        </div>
      )}

      {/* Header — full width, always on top (hidden on mobile for chats to avoid double headers) */}
      <div className={isChatPage ? 'hidden lg:block' : 'block'}>
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} toggleSidebarCollapse={toggleSidebarCollapse} isSidebarCollapsed={isCollapsed} onOpenNotes={() => setNotesDrawerOpen(true)} />
      </div>

      {/* Content area: Sidebar + Main */}
      <div className="flex flex-1 overflow-hidden">
        <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} toggleCollapse={toggleSidebarCollapse} />
        <div className={`relative flex flex-1 flex-col bg-white dark:bg-boxdark-2 ${isChatPage ? 'overflow-hidden h-full' : 'overflow-y-auto overflow-x-hidden'}`}>
          <main className={isChatPage ? 'h-full w-full' : 'w-full'}>
            <div className={`mx-auto w-full ${isChatPage ? 'p-0 h-full max-w-full' : 'p-0 max-w-full'} relative`}>
              <ErrorBoundary>
                <Suspense fallback={<SkeletonTable columns={5} rows={6} />}>
                  <Outlet />
                </Suspense>
              </ErrorBoundary>
            </div>
          </main>
          <ToastContainer />
          <AdvisorNotesDrawer open={notesDrawerOpen} onToggle={setNotesDrawerOpen} />
        </div>
      </div>
    </div>
  </div>
}